import React, { useEffect, useRef, useState } from "react";

interface VariableTextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  type?: string;
  /** How a dropped variable combines with the current value. Default: append. */
  dropMode?: "append" | "replace";
}

/**
 * Text input that reliably accepts drag-and-drop of workflow variables from
 * the NodeDataSidebar.
 *
 * Why native listeners bound ONCE via refs: React's synthetic onDrop is
 * unreliable on native <input> elements, and re-binding listeners on every
 * render (or churning a dragOver state) can remove the drop handler at the
 * exact moment the browser fires `drop`. We bind real DOM listeners a single
 * time on mount and read the latest value/onChange through refs.
 */
export const VariableTextInput: React.FC<VariableTextInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className = "",
  type = "text",
  dropMode = "append",
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Latest props kept in refs so the once-bound listeners stay current.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const dropModeRef = useRef(dropMode);
  dropModeRef.current = dropMode;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onDragOver = (e: DragEvent) => {
      if (disabledRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    };
    const onDragLeave = () => setDragOver(false);
    const onDrop = (e: DragEvent) => {
      if (disabledRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const v = e.dataTransfer?.getData("text/plain") || "";
      if (!v) return;
      const next = dropModeRef.current === "replace" ? v : (valueRef.current || "") + v;
      onChangeRef.current(next);
    };

    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragenter", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragenter", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
    // Bind once — refs carry the latest props.
  }, []);

  return (
    <input
      ref={ref}
      type={type}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} ${dragOver ? "ring-2 ring-sky-400 border-transparent" : ""}`}
    />
  );
};

export default VariableTextInput;
